import moment from 'moment';
import { isNil } from 'lodash';
import { ELSURLHelper } from '@els/els-ui-common-react';
import { ELSStepperItem } from '@els/els-component-shared-ts-react';
import {
  AssessmentStatusDto,
  AssignmentDto
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { BaseAssignmentEditorState } from './withBaseAssignmentEditor.hoc';
import { AssignmentEditorSearchParam } from './with-base-assignment-editor.constants';
import { AssignmentAttemptLimit } from '../../components/assignment-editor/assignment-editor.constant';
import { isOnlyNumber } from '../../utilities/common.utilities';
import { UserDto } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import {
  PostSaveAssignment,
  StepSubtitleHandler
} from './with-base-assignment-editor.models';
import { AnalyticsAction } from '../../models/analytics.models';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { FeatureFlagsGroupedDto } from '../../apis/eols-features-api/eols-features-api.dtos';
import { getParentLinkId } from '../../utilities/app.utilities';

export const isCustomGradePointsEnabled = (courseSectionId: string, featureFlagsGrouped: FeatureFlagsGroupedDto[]) => {
  return getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.IS_CUSTOM_GRADE_POINTS_ENABLED,
    courseSectionId
  );
};

// requires global flag `IS_CUSTOM_GRADE_POINTS_ENABLED` to be enabled
export const isEaqCustomGradePointsEnabled = (courseSectionId: string, featureFlagsGrouped: FeatureFlagsGroupedDto[]) => {
  return isCustomGradePointsEnabled(courseSectionId, featureFlagsGrouped)
    && getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
      featureFlagsGrouped,
      FEATURE_FLAG.IS_EAQ_CUSTOM_GRADE_POINTS_ENABLED,
      courseSectionId
    );
};

export const updateStepsOnNav = (steps: ELSStepperItem[], newActiveIndex: number): ELSStepperItem[] => {
  return steps.map((step, idx) => {
    return {
      ...step,
      isActive: idx === newActiveIndex,
      isClickable: idx < newActiveIndex,
      isComplete: idx < newActiveIndex
    };
  });
};

export const updateEaqStepsOnEdit = (steps: ELSStepperItem[], newActiveIndex: number): ELSStepperItem[] => {
  return steps.map((step, idx) => {
    return {
      ...step,
      isActive: idx === newActiveIndex,
      isClickable: idx !== newActiveIndex,
      isComplete: idx < newActiveIndex
    };
  });
};

export const getStepFromLocation = (): number => {
  const activeStepIndex = ELSURLHelper.getParameterByName(AssignmentEditorSearchParam.ACTIVE_STEP_INDEX);
  if (isNil(activeStepIndex)) {
    return 0;
  }
  if (!isOnlyNumber(activeStepIndex)) {
    return 0;
  }
  const num = parseInt(activeStepIndex, 10);
  if (isNaN(num)) {
    return 0;
  }
  return num;
};

export const getGoalIdFromVtwId = (
  assignment: Partial<AssignmentDto>,
  vtwId: string
): number => {

  // This method assumes there is a 1on1 relationship between goals and vtwIds

  if (!assignment || !assignment.assignmentGoals) {
    return null;
  }

  const existingGoal = assignment.assignmentGoals.find((goal) => {
    return goal.vtwId === vtwId;
  });

  if (!existingGoal) {
    return null;
  }

  return existingGoal.id;
};

export const getTopicIdFromVtwId = (
  assignment: Partial<AssignmentDto>,
  vtwId: string
): number => {

  // This method assumes there is a 1on1 relationship between goals and vtwIds

  if (!assignment || !assignment.assignmentTopics) {
    return null;
  }

  const existingTopic = assignment.assignmentTopics.find((topic) => {
    return topic.vtwId === vtwId;
  });

  if (!existingTopic) {
    return null;
  }

  return existingTopic.id;
};

export const getDefaultAssignmentEditorState = (): BaseAssignmentEditorState => {
  return {
    activeStepIndex: getStepFromLocation(),
    assignmentId: ELSURLHelper.getParameterByName(AssignmentEditorSearchParam.ASSIGNMENT_ID),
    assignmentType: null,
    assignment: null,
    isAvailableDateRequired: false,
    hasAvailableDate: true,
    hasDueDate: false,
    pageTitle: 'Page Title',
    parentSyllabusItemId: ELSURLHelper.getParameterByName(AssignmentEditorSearchParam.PARENT_SYLLABUS_ITEM_ID),
    showDisplayName: false,
    showDueDate: false,
    isDueDateRequired: false,
    showGrading: false,
    steps: [],
    syllabusItemId: ELSURLHelper.getParameterByName(AssignmentEditorSearchParam.SYLLABUS_ITEM_ID),
    isReadOnlyDisplayName: false,
    isUnassignable: false,
    newSyllabusItemType: null,
    existingAssignment: null,
    selectedStudents: [],
    studyMode: false,
    isInitialStateSet: false,
    attemptLimit: AssignmentAttemptLimit.UNLIMITED,
    numberOfAttempts: '',
  };
};

export const getDefaultDueDateMomentIns = (availableDate?: string | Date): moment.Moment => {
  return moment(availableDate)
    .endOf('day').add(6, 'days');
};

export const getDefaultDueDateString = (availableDate?: string | Date): string => {
  return getDefaultDueDateMomentIns(availableDate)
    .toISOString();
};

export const getCurrentDateISOString = (): string => {
  return moment()
    .toISOString();
};

export const isStudentOptionDisabled = (props: {
  assignment: AssignmentDto;
  student: UserDto;
}) => {
  const {
    assignment,
    student
  } = props;

  if (!student) {
    return true;
  }

  if (!assignment || !assignment.studentAssignments || !assignment.studentAssignments.length) {
    return false;
  }

  return assignment.studentAssignments.filter((stuAssignment) => {
    return stuAssignment.userId === student.id;
  })
    .some((stuAssignment) => {
      return stuAssignment.status !== AssessmentStatusDto.NOT_STARTED;
    });
};

export const postSaveHandler: PostSaveAssignment = (props) => {
  const linkId = getParentLinkId(props.appLinkData, props.appLinkCookies);

  if (!linkId) {
    // eslint-disable-next-line no-console
    console.log('Missing link id');
    return;
  }

  const appLinkConfig = {
    linkId,
    assignmentIds: props.assignment && props.assignment.id ? [props.assignment.id] : null
  };

  props.trackAction({
    action: AnalyticsAction.ASSIGNMENT_EDITOR_POST_HANDLED,
    props: {
      ref: 'EAQ_ASSIGNMENT_EDITOR',
      assignmentIds: appLinkConfig.assignmentIds
    }
  });

  props.returnAppLink(appLinkConfig);
};

export const defaultStepSubtitleHandler: StepSubtitleHandler = (step) => {
  return step ? step.subtitle : '';
};
